import React from 'react';
import { Box, Typography } from '@mui/material';
import WifiOffRounded from '@mui/icons-material/WifiOffRounded';

// ─── Fixture status adapter ───────────────────────────────────────────────────

export function getFixtureStatus(state) {
  if (!state) return { status: 'NS', minute: null };
  if ('notStarted' in state) return { status: 'NS', minute: null };
  if ('tbd' in state) return { status: 'TBD', minute: null };
  if ('postponed' in state) return { status: 'PST', minute: null };
  if ('cancelled' in state) return { status: 'CANC', minute: null };
  if ('abandoned' in state) return { status: 'ABD', minute: null };
  if ('suspended' in state) return { status: 'SUSP', minute: null };
  if ('interrupted' in state) return { status: 'INT', minute: null };
  if ('awarded' in state) return { status: 'AWD', minute: null };
  if ('finished' in state) {
    const f = state.finished;
    if (f && 'penalties' in f) return { status: 'PEN', minute: null };
    if (f && 'afterExtraTime' in f) return { status: 'AET', minute: null };
    return { status: 'FT', minute: null };
  }
  if ('live' in state) {
    const d = state.live?.details ?? {};
    if ('firstHalf' in d) return { status: '1H', minute: String(d.firstHalf.elapsed) };
    if ('secondHalf' in d) return { status: '2H', minute: String(d.secondHalf.elapsed) };
    if ('halfTime' in d) return { status: 'HT', minute: null };
    if ('extraTime' in d) return { status: 'ET', minute: String(d.extraTime.elapsed) };
    if ('breakTime' in d) return { status: 'BT', minute: null };
    if ('penaltyShootout' in d) return { status: 'P', minute: null };
    return { status: 'LIVE', minute: null };
  }
  return { status: 'NS', minute: null };
}

// ─── Shield placeholder ───────────────────────────────────────────────────────

function ShieldPlaceholder({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M32 4L8 14v18c0 13 10.5 22.5 24 28 13.5-5.5 24-15 24-28V14L32 4z" fill="#d0d0d0" />
      <path d="M32 4L56 14v18c0 13-10.5 22.5-24 28V4z" fill="#b8b8b8" />
      <path d="M32 32H8c0 13 10.5 22.5 24 28V32z" fill="#b8b8b8" />
      <path d="M32 4v28H8V14L32 4z" fill="#d0d0d0" />
    </svg>
  );
}

// ─── Team badge ───────────────────────────────────────────────────────────────

export function TeamBadgeImage({ team, size = 64 }) {
  const [status, setStatus] = React.useState('loading'); // 'loading' | 'loaded' | 'error'

  return (
    <Box sx={{ width: size, height: size, position: 'relative', flexShrink: 0 }}>
      {status !== 'loaded' && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
          <ShieldPlaceholder size={size * 0.65} />
        </Box>
      )}
      {team.badgeUrl && (
        <Box
          component="img"
          src={team.badgeUrl}
          alt={team.name}
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', opacity: status === 'loaded' ? 1 : 0, transition: 'opacity 0.2s ease' }}
        />
      )}
    </Box>
  );
}

function TeamBadge({ team, align = 'center' }) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center', gap: 0.5 }}>
      <TeamBadgeImage team={team} size={64} />
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, textAlign: align, wordBreak: 'break-word' }}>
        {team.name}
      </Typography>
    </Box>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status, minute }) {
  const isAddedTime = minute && minute.includes('+');

  let label = null;
  let variant = null; // 'live' | 'added' | 'finished' | 'text' | 'icon' | null

  switch (status) {
    case '1H':
    case '2H':
      label = `${minute}'`;
      variant = isAddedTime ? 'added' : 'live';
      break;
    case 'HT':
      label = 'HT';
      variant = 'live';
      break;
    case 'ET':
      label = `${minute}'`;
      variant = isAddedTime ? 'added' : 'live';
      break;
    case 'BT':
      label = 'HT';
      variant = 'live';
      break;
    case 'P':
      label = 'PEN';
      variant = 'live';
      break;
    case 'LIVE':
      label = 'Live';
      variant = 'live';
      break;
    case 'FT':
      label = 'FT';
      variant = 'finished';
      break;
    case 'AET':
      label = 'FT-AET';
      variant = 'finished';
      break;
    case 'PEN':
      label = 'FT-PENS';
      variant = 'finished';
      break;
    case 'PST':
      label = 'Postponed';
      variant = 'text';
      break;
    case 'CANC':
      label = 'Canceled';
      variant = 'text';
      break;
    case 'ABD':
      label = 'Abandoned';
      variant = 'text';
      break;
    case 'WO':
      label = 'Forfeit';
      variant = 'text';
      break;
    case 'SUSP':
      label = 'Suspended';
      variant = 'text';
      break;
    case 'INT':
      label = 'Interrupted';
      variant = 'text';
      break;
    case 'AWD':
      variant = 'icon';
      break;
    default:
      return null;
  }

  if (variant === 'icon') {
    return <WifiOffRounded sx={{ color: 'text.disabled', fontSize: 22, mt: 0.5 }} />;
  }

  if (variant === 'text') {
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled', mt: 0.5 }}>
        {label}
      </Typography>
    );
  }

  const bg = variant === 'finished' ? '#111' : variant === 'added' ? '#ef5350' : '#4caf50';
  const border = variant === 'finished' ? '1px solid rgba(255,255,255,0.18)' : 'none';

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.3, mt: 0.75, bgcolor: bg, border, borderRadius: 999 }}>
      <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.72rem', letterSpacing: 0.3 }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── Score centre ─────────────────────────────────────────────────────────────

function formatKickoffTime(date) {
  if (!date) return '--:--';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatKickoffDate(date) {
  if (!date) return '';
  const now = new Date();
  const diffDays = Math.round((date - now) / 86400000);
  if (diffDays < 1) return 'Today';
  if (diffDays < 2) return 'Tomorrow';
  return date.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
}

function ScoreCentre({ fixture }) {
  const { status, minute, homeTeam, awayTeam, aggregate, kickoff } = fixture;
  const isUpcoming = status === 'TBD' || status === 'NS';

  if (isUpcoming) {
    return (
      <Box sx={{ textAlign: 'center', minWidth: 120 }}>
        {aggregate && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.25 }}>
            first leg {aggregate.home} : {aggregate.away}
          </Typography>
        )}
        {status === 'TBD' ? (
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>TBD</Typography>
        ) : (
          <>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
              {formatKickoffTime(kickoff)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {formatKickoffDate(kickoff)}
            </Typography>
          </>
        )}
      </Box>
    );
  }

  const hasPens = homeTeam.penaltyScore != null && awayTeam.penaltyScore != null;

  return (
    <Box sx={{ textAlign: 'center', minWidth: 160 }}>
      {aggregate && (
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.25 }}>
          {aggregate.label} {aggregate.home} : {aggregate.away}
        </Typography>
      )}
      <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1, color: 'text.primary', letterSpacing: 2 }}>
        {hasPens && (
          <Box component="span" sx={{ fontSize: '0.5em', verticalAlign: 'middle', mr: 0.5 }}>
            ({homeTeam.penaltyScore})
          </Box>
        )}
        {homeTeam.score} : {awayTeam.score}
        {hasPens && (
          <Box component="span" sx={{ fontSize: '0.5em', verticalAlign: 'middle', ml: 0.5 }}>
            ({awayTeam.penaltyScore})
          </Box>
        )}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <StatusBadge status={status} minute={minute} />
      </Box>
    </Box>
  );
}

// ─── Score header ─────────────────────────────────────────────────────────────

export default function ScoreHeader({ fixture }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 2, sm: 4 }, py: 3, px: 2 }}>
      <TeamBadge team={fixture.homeTeam} align="right" />
      <ScoreCentre fixture={fixture} />
      <TeamBadge team={fixture.awayTeam} align="left" />
    </Box>
  );
}
